import type { ComponentStyleConfig, SystemProps } from '@chakra-ui/react';
import type { StyleFunctionProps } from '@chakra-ui/theme-tools';

import { getColor } from '../../helpers/utils';

export const baseControlWrapperTheme = (props: StyleFunctionProps): SystemProps => ({
    display: 'flex',
    zIndex: 1,
    w: '100%',
    height: '56px',
    border: 'none',
    boxSizing: 'border-box',
    boxShadow: `inset 0 0 0 1px ${getColor(props.theme, 'neutral.300')}`,
    fontStyle: 'normal',
    fontWeight: 'normal',
    color: 'neutral.900',
    outline: 'none',
    borderRadius: 'xl',
    _disabled: {
        border: '1px dashed',
        borderColor: 'neutral.300',
        cursor: 'not-allowed',
        boxShadow: 'none !important',
        color: 'neutral.400'
    },
    _hover: { boxShadow: `inset 0 0 0 1px ${getColor(props.theme, 'neutral.600')}` },
    _readOnly: { userSelect: 'all' },
    _invalid: { boxShadow: `inset 0 0 0 1px ${getColor(props.theme, 'error.700')}` },
    _focus: {
        zIndex: 1,
        boxShadow: `inset 0 0 0 2px ${getColor(props.theme, 'primary.500')}`
    }
});

const commonTextStyles: SystemProps = {
    marginTop: '3px',
    width: '100%',
    px: '14px',
    fontStyle: 'normal',
    fontWeight: '400',
    fontSize: '12px',
    letterSpacing: '0.4px',
    lineHeight: '20px'
};

/**
 * BaseControl theme that can be used for a common look and feel eg,
 * Select, TextInput and third party components like credit card.
 *
 * @see https://github.com/chakra-ui/chakra-ui/blob/092b20f3e93064c1db312062b76b420a7ed1703b/packages/theme/src/components/input.ts
 * @see https://www.figma.com/file/AVVZO09XE8fZ0uuOaJYcQg/B2C-Onboarding---for-Dev?node-id=2705%3A56301
 */
export const BaseControl: ComponentStyleConfig = {
    parts: [
        'Wrapper',
        'FormControl',
        'FormLabel',
        'FormHelperText',
        'FormErrorMessage',
        'InputRightElement',
        'InputLeftElement'
    ],
    baseStyle: ({
        isFocused,
        isInvalid,
        isDisabled,
        isWarning,
        isSuccess,
        isUserWarning,
        isHovering,
        isLabelOnTop,
        hasRightAdornment,
        theme
    }) => {
        // Manually get colors for theming boxShadow, a current limitation of Chakra api
        const warning500 = getColor(theme, 'warning.500');
        const secondary400 = getColor(theme, 'secondary.400');
        const success500 = getColor(theme, 'success.500');
        const neutral300 = getColor(theme, 'neutral.300');
        const neutral600 = getColor(theme, 'neutral.600');
        const error700 = getColor(theme, 'error.700');
        const primary500 = getColor(theme, 'primary.500');
        const formLabelColor = (): SystemProps['color'] => {
            if (isWarning && isLabelOnTop) {
                return 'warning.700';
            }
            if (isUserWarning && isLabelOnTop) {
                return 'secondary.700';
            }
            if (isSuccess && !isFocused && isLabelOnTop) {
                return 'success.700';
            }
            if (isInvalid) {
                return isLabelOnTop ? 'error.700' : 'neutral.600';
            }

            return isFocused ? 'primary.700' : 'neutral.600';
        };
        const helperTextColor = (): SystemProps['color'] => {
            if (isWarning) {
                return 'warning.700';
            }
            if (isUserWarning) {
                return 'secondary.700';
            }
            if (isSuccess && !isFocused) {
                return 'success.700';
            }

            return isDisabled ? 'neutral.400' : 'neutral.600';
        };
        const inputBorderColor = (): SystemProps['boxShadow'] => {
            if (isInvalid) {
                return `inset 0 0 0 1px ${error700}`;
            }

            if (isWarning && !isFocused) {
                return `inset 0 0 0 1px ${warning500}`;
            }

            if (isUserWarning && !isFocused) {
                return `inset 0 0 0 1px ${secondary400}`;
            }

            if (isSuccess && !isFocused) {
                return `inset 0 0 0 1px ${success500}`;
            }

            if (isHovering && !isFocused) {
                return `inset 0 0 0 1px ${neutral600}`;
            }

            return `inset 0 0 0 1px ${neutral300}`;
        };
        const Wrapper = {
            ...baseControlWrapperTheme({
                theme
            } as any),
            boxShadow: inputBorderColor(),
            _focus: {
                zIndex: 1,
                boxShadow: isInvalid ? `inset 0 0 0 1px ${error700}` : `inset 0 0 0 2px ${primary500}`
            }
        };
        // When there is an adornment to the right there is whitespace issues,
        // but only when label is not on top.
        const marginRight = isLabelOnTop ? '16px' : hasRightAdornment ? '42px' : '16px';

        return {
            FormControl: {
                position: 'relative',
                w: '100%'
            },
            Wrapper,
            InputLeftElement: {
                h: 14,
                pointerEvents: 'none'
            },
            InputRightElement: {
                h: 14,
                pointerEvents: 'none'
            },
            FormLabel: {
                position: 'absolute',
                zIndex: 2,
                top: isLabelOnTop ? '2px' : '28px',
                left: '11px',
                paddingLeft: '4px',
                paddingRight: '4px',
                marginRight,
                transform: 'translate(0, -50%)',
                transition: 'all 0.2s ease-in-out',
                pointerEvents: 'none',
                fontSize: isLabelOnTop ? '12px' : '16px',
                fontStyle: 'normal',
                fontWeight: '400',
                letterSpacing: '0.15px',
                background: 'white !important',
                color: formLabelColor(),
                _disabled: {
                    color: 'neutral.400',
                    opacity: 1
                }
            },
            FormHelperText: {
                ...commonTextStyles,
                color: helperTextColor()
            },
            FormErrorMessage: {
                ...commonTextStyles,
                color: isDisabled ? 'neutral.400' : 'error.700'
            }
        };
    }
};
